import apiClient from './api';

/**
 * Layer groups ("My menus"): a person's own menus of layers, shareable with chosen colleagues or the whole company
 * (geonexora-server docs/layer-groups-api.md). A group is a view onto layers that exist anyway - it never draws a
 * layer of its own; switching an item on switches its layer on, wherever else it is listed.
 */

export type GroupItemKind = 'platform' | 'company';

/** What a group item names: a platform layer by its GeoServer name (cite:GeoMahalle), a company layer by its key. */
export interface GroupItemRef {
  kind: GroupItemKind;
  ref: string;
}

export interface GroupItemLayer {
  /** Platform layer: the layer list's id. */
  number?: number;
  /** Company layer: its key and workspace. */
  key?: string;
  workspace?: string;
  name: string;
  labels?: Record<string, string>;
  geometry?: string;
}

export interface GroupItem extends GroupItemRef {
  /** The owner's own name for the layer in this menu; null = the layer's name. */
  label?: string | null;
  /** The owner sees this layer only in this menu, not also in its usual place (their own tree only). */
  onlyHere: boolean;
  layer: GroupItemLayer;
}

export interface GroupPerson {
  subject: string;
  name?: string | null;
}

export interface LayerGroup {
  id: string;
  name: string;
  description?: string | null;
  colour?: string | null;
  icon?: string | null;
  position: number;
  owner: string;
  ownerName?: string | null;
  mine: boolean;
  editable: boolean;
  canShare: boolean;
  hidden: boolean;
  collapsed: boolean;
  shared: boolean;
  /** Owner only: who it is shared with. */
  sharing?: { company: boolean; people: GroupPerson[] };
  /** Recipient only: how it reached them. */
  sharedWith?: 'company' | 'person';
  /** Owner only: layers in it they can no longer see (deleted, or a right taken away). */
  unavailable?: number;
  items: GroupItem[];
}

export interface LayerGroups {
  me: string;
  canCreate: boolean;
  canShare: boolean;
  groups: LayerGroup[];
}

/** An item as it is sent: the layer, and the owner's choices for it. */
export interface GroupItemInput extends GroupItemRef {
  label?: string | null;
  onlyHere?: boolean;
}

export interface GroupChange {
  name?: string;
  description?: string;
  colour?: string;
  icon?: string;
  /** The whole list in its new order. */
  items?: GroupItemInput[];
}

const BASE = '/v2/layer-groups';

export const toInput = (i: GroupItem): GroupItemInput => ({ kind: i.kind, ref: i.ref, label: i.label ?? null, onlyHere: i.onlyHere });

export const layerGroupsService = {
  async list(): Promise<LayerGroups> {
    return (await apiClient.get<LayerGroups>(BASE)).data;
  },
  async create(name: string, items: GroupItemInput[] = []): Promise<LayerGroup> {
    return (await apiClient.post<LayerGroup>(BASE, { name, items })).data;
  },
  async change(id: string, change: GroupChange): Promise<LayerGroup> {
    return (await apiClient.put<LayerGroup>(`${BASE}/${id}`, change)).data;
  },
  async remove(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`);
  },
  async order(ids: string[]): Promise<LayerGroups> {
    return (await apiClient.put<LayerGroups>(`${BASE}/order`, { ids })).data;
  },
  async share(id: string, company: boolean, people: GroupPerson[]): Promise<LayerGroup> {
    return (await apiClient.put<LayerGroup>(`${BASE}/${id}/share`, { company, people })).data;
  },
  async view(id: string, view: { hidden?: boolean; collapsed?: boolean }): Promise<LayerGroup> {
    return (await apiClient.put<LayerGroup>(`${BASE}/${id}/view`, view)).data;
  },
  async copy(id: string, name?: string): Promise<LayerGroup> {
    return (await apiClient.post<LayerGroup>(`${BASE}/${id}/copy`, name ? { name } : {})).data;
  },
};

/** The refusal's code, for the screen to word (`layerGroups.error.<code>`), or its message. */
export function groupError(e: any): string {
  return e?.response?.data?.code ?? e?.response?.data?.message ?? e?.message ?? 'error';
}

export default layerGroupsService;
